import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Flex,
    Spinner,
    Text,
    useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Pagination from '../../../components/Pagination';
import SessionContext from '../../../contexts/SessionContext';
import { useGetPurchaseOrdersFeed } from '../../../hooks/inventory/useGetPurchaseOrdersFeed';
import { useSubmitPurchaseOrder } from '../../../hooks/inventory/useSubmitPurchaseOrder';
import { Button } from '../../../ui';
import {
    PurchaseOrderEntry,
    PurchaseOrderPart,
    SubmitPurchaseOrderInput,
    SubmitPurchaseOrderPart,
} from '../../../types/inventory/purchaseOrders';
import type { PurchaseOrderPartAction } from './PurchaseOrderPartCells';
import type { PurchaseOrderStatusFilterValue } from './PurchaseOrderStatusFilter';
import { PurchaseOrdersTable } from './PurchaseOrdersTable';

type DirtyState = Record<string, Record<string, PurchaseOrderPartAction>>;
type DaysDraft = Record<string, Record<string, number>>;
type SelectedState = Record<string, Set<string>>;

/** Feed page size; the API accepts up to 100. */
const PAGE_SIZE = 25;

function extractApiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        return data?.message?.trim() || fallback;
    }
    if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { data?: { message?: string } } }).response?.data?.message
    ) {
        return String(
            (error as { response: { data: { message: string } } }).response.data.message,
        ).trim();
    }
    if (error instanceof Error && error.message.trim()) {
        return error.message.trim();
    }
    return fallback;
}

interface InventoryPurchaseOrdersPanelProps {
    locationIds: Array<string> | 'all';
    purchaseOrderId?: string | null;
    searchQuery?: string;
    statusFilter?: PurchaseOrderStatusFilterValue;
    /** Toolbar element the Submit button renders into (left of the location selector). */
    submitSlot?: HTMLElement | null;
}

export function InventoryPurchaseOrdersPanel({
    locationIds,
    purchaseOrderId = null,
    searchQuery = '',
    statusFilter = 'all',
    submitSlot = null,
}: InventoryPurchaseOrdersPanelProps) {
    const toast = useToast();
    const sessionContext = useContext(SessionContext);
    const canOpenOrder = sessionContext?.organizationMode === 'full-time';
    const submitMutation = useSubmitPurchaseOrder();
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery.trim());
    const [dirtyByOrder, setDirtyByOrder] = useState<DirtyState>({});
    const [daysByOrder, setDaysByOrder] = useState<DaysDraft>({});
    const [selectedByOrder, setSelectedByOrder] = useState<SelectedState>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    /** `pageCursors[i]` is the cursor that loads page i; page 0 has none. */
    const [pageCursors, setPageCursors] = useState<Array<string | undefined>>([undefined]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 250);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // A deep link fetches just that PO, with every part, so it is always found.
    const feedStatusFilter = purchaseOrderId ? 'all' : statusFilter;
    const locationKey = Array.isArray(locationIds) ? locationIds.join(',') : locationIds;

    useEffect(() => {
        setPageIndex(0);
        setPageCursors([undefined]);
    }, [feedStatusFilter, locationKey, debouncedSearch, purchaseOrderId]);

    // No keepPreviousPage: page changes wipe the table and show a spinner
    // (same as Orders / Home / Delivery Board feeds).
    const feed = useGetPurchaseOrdersFeed({
        statusFilter: feedStatusFilter,
        locationIds,
        search: debouncedSearch || undefined,
        purchaseOrderId: purchaseOrderId || undefined,
        cursor: purchaseOrderId ? undefined : pageCursors[pageIndex],
        limit: PAGE_SIZE,
    });

    const nextCursor = feed.data?.nextCursor;
    useEffect(() => {
        if (!nextCursor || feed.isFetching) return;
        setPageCursors((prev) => {
            if (prev[pageIndex + 1] === nextCursor) return prev;
            const next = prev.slice(0, pageIndex + 1);
            next[pageIndex + 1] = nextCursor;
            return next;
        });
    }, [nextCursor, feed.isFetching, pageIndex]);

    const hasNextPage = !!feed.data?.nextCursor;
    const currentPage = pageIndex + 1;
    const totalPages = Math.max(1, hasNextPage ? currentPage + 1 : currentPage);

    const handlePageChange = (page: number) => {
        const target = page - 1;
        if (target < 0 || target >= pageCursors.length) return;
        if (target > 0 && !pageCursors[target]) return;
        setPageIndex(target);
        setSelectedByOrder({});
    };

    // Feed hook already collapses cost-lot siblings into one row per partId.
    const requests = useMemo(() => {
        const rows = feed.data?.requests ?? [];
        const partsForFilter = (parts: PurchaseOrderPart[]) => {
            if (statusFilter === 'all') return parts;
            if (statusFilter === 'pending') {
                return parts.filter((part) => part.status === 'pending');
            }
            return parts.filter(
                (part) => part.status === 'confirmed' || part.status === 'rejected',
            );
        };

        // API returns part subsets for pending/completed; filter again so a
        // deep-link `all` fetch also respects the status filter.
        const withParts = rows
            .map((row) => ({ ...row, parts: partsForFilter(row.parts) }))
            .filter((row) => row.parts.length > 0);
        if (!purchaseOrderId) return withParts;

        // The deep-linked PO stays visible with all its parts even if the filter would hide it.
        if (withParts.some((row) => row.purchaseOrderId === purchaseOrderId)) return withParts;
        const focused = rows.find((row) => row.purchaseOrderId === purchaseOrderId);
        return focused ? [focused, ...withParts] : withParts;
    }, [feed.data?.requests, statusFilter, purchaseOrderId]);

    // After a submit empties the last page, step back so the footer stays usable.
    // Skip while submitting: optimistic cache can empty the page briefly; on failure
    // we roll back and must stay on the same pageIndex.
    useEffect(() => {
        if (isSubmitting || feed.isFetching || !feed.data) return;
        if (pageIndex > 0 && feed.data.requests.length === 0) {
            setPageIndex((prev) => Math.max(0, prev - 1));
        }
    }, [feed.data, feed.isFetching, isSubmitting, pageIndex]);

    useEffect(() => {
        // Changing the status filter clears selection scoped to the previous result set.
        setSelectedByOrder({});
    }, [statusFilter]);

    useEffect(() => {
        setDaysByOrder((prev) => {
            const next: DaysDraft = { ...prev };
            for (const request of feed.data?.requests ?? []) {
                const existing = next[request.purchaseOrderId] ?? {};
                const merged: Record<string, number> = { ...existing };
                for (const part of request.parts) {
                    if (merged[part.partId] === undefined) {
                        merged[part.partId] = part.deliveryDays;
                    }
                }
                next[request.purchaseOrderId] = merged;
            }
            return next;
        });
    }, [feed.data?.requests]);

    const dirtyPartCount = useMemo(() => {
        let count = 0;
        for (const parts of Object.values(dirtyByOrder)) {
            count += Object.keys(parts).length;
        }
        return count;
    }, [dirtyByOrder]);

    const canSubmit = useMemo(() => {
        if (dirtyPartCount === 0) return false;
        for (const [orderId, actions] of Object.entries(dirtyByOrder)) {
            const days = daysByOrder[orderId] ?? {};
            for (const [partId, action] of Object.entries(actions)) {
                if (action !== 'confirm') continue;
                const d = days[partId];
                if (!Number.isInteger(d) || d < 0) return false;
            }
        }
        return true;
    }, [dirtyByOrder, daysByOrder, dirtyPartCount]);

    const setPartAction = (
        purchaseOrderIdKey: string,
        partId: string,
        action: PurchaseOrderPartAction | undefined,
    ) => {
        setDirtyByOrder((prev) => {
            const orderDirty = { ...(prev[purchaseOrderIdKey] ?? {}) };
            if (!action) delete orderDirty[partId];
            else orderDirty[partId] = action;
            const next = { ...prev };
            if (Object.keys(orderDirty).length === 0) delete next[purchaseOrderIdKey];
            else next[purchaseOrderIdKey] = orderDirty;
            return next;
        });
    };

    const setDays = (purchaseOrderIdKey: string, partId: string, days: number) => {
        setDaysByOrder((prev) => ({
            ...prev,
            [purchaseOrderIdKey]: {
                ...(prev[purchaseOrderIdKey] ?? {}),
                [partId]: days,
            },
        }));
    };

    const togglePartSelected = (
        purchaseOrderIdKey: string,
        partId: string,
        checked: boolean,
    ) => {
        setSelectedByOrder((prev) => {
            const nextSet = new Set(prev[purchaseOrderIdKey] ?? []);
            if (checked) nextSet.add(partId);
            else nextSet.delete(partId);
            const next = { ...prev };
            if (nextSet.size === 0) delete next[purchaseOrderIdKey];
            else next[purchaseOrderIdKey] = nextSet;
            return next;
        });
    };

    const toggleSelectAllPending = (request: PurchaseOrderEntry, checked: boolean) => {
        const pendingIds = request.parts
            .filter((p) => p.status === 'pending')
            .map((p) => p.partId);
        setSelectedByOrder((prev) => {
            const next = { ...prev };
            if (!checked) {
                delete next[request.purchaseOrderId];
                return next;
            }
            next[request.purchaseOrderId] = new Set(pendingIds);
            return next;
        });
    };

    const selectedTotal = useMemo(() => {
        let count = 0;
        for (const set of Object.values(selectedByOrder)) {
            count += set.size;
        }
        return count;
    }, [selectedByOrder]);

    const clearSelection = () => setSelectedByOrder({});

    const applyBulkAction = (action: PurchaseOrderPartAction) => {
        setDirtyByOrder((prev) => {
            const next = { ...prev };
            for (const request of requests) {
                const selected = selectedByOrder[request.purchaseOrderId];
                if (!selected?.size) continue;
                const orderDirty = { ...(next[request.purchaseOrderId] ?? {}) };
                for (const part of request.parts) {
                    if (part.status !== 'pending') continue;
                    if (!selected.has(part.partId)) continue;
                    orderDirty[part.partId] = action;
                }
                next[request.purchaseOrderId] = orderDirty;
            }
            return next;
        });
        clearSelection();
    };

    const handleGlobalSubmit = async () => {
        if (!canSubmit) {
            toast({
                title: 'ETA required',
                description: 'Set delivery days for every part marked Confirm before submitting.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const payloads: SubmitPurchaseOrderInput[] = Object.entries(dirtyByOrder).map(
            ([orderId, actions]) => {
                const days = daysByOrder[orderId] ?? {};
                const parts: SubmitPurchaseOrderPart[] = Object.entries(actions).map(
                    ([partId, action]) => {
                        if (action === 'confirm') {
                            return {
                                partId,
                                action: 'confirm',
                                deliveryDays: days[partId],
                            };
                        }
                        return { partId, action: 'reject' };
                    },
                );
                return { purchaseOrderId: orderId, parts };
            },
        );

        setIsSubmitting(true);
        try {
            for (const payload of payloads) {
                // onMutate patches part statuses immediately; Action cells follow
                // part.status (not dirty) once the feed cache updates.
                await submitMutation.mutateAsync(payload);
                setDirtyByOrder((prev) => {
                    if (!(payload.purchaseOrderId in prev)) return prev;
                    const next = { ...prev };
                    delete next[payload.purchaseOrderId];
                    return next;
                });
                setSelectedByOrder((prev) => {
                    if (!(payload.purchaseOrderId in prev)) return prev;
                    const next = { ...prev };
                    delete next[payload.purchaseOrderId];
                    return next;
                });
            }
            toast({
                title: 'Purchase orders updated',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to submit purchase orders',
                description: extractApiErrorMessage(error, 'Please try again.'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const showSubmit = canSubmit || isSubmitting;

    return (
        <Flex
            direction="column"
            flex={1}
            h="full"
            minH={0}
            overflow="hidden"
            borderTop="1px solid"
            borderColor="border.subtle"
        >
            {submitSlot && showSubmit
                ? createPortal(
                    <Button
                        size="sm"
                        ml={2}
                        flexShrink={0}
                        onClick={handleGlobalSubmit}
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                        loadingText="Submitting..."
                    >
                        Submit
                    </Button>,
                    submitSlot,
                )
                : null}

            {feed.isLoading || (!feed.data && !feed.isError) ? (
                <Flex justify="center" align="center" flex={1} minH="400px">
                    <Spinner size="xl" color="brand.solid" />
                </Flex>
            ) : feed.isError ? (
                <Box px={6} pt={4}>
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                            Could not load purchase orders. Please try again.
                        </AlertDescription>
                    </Alert>
                </Box>
            ) : requests.length === 0 ? (
                <Flex justify="center" align="center" py={16}>
                    <Text color="fg.muted" fontSize="sm">
                        {statusFilter === 'pending'
                            ? 'No pending purchase orders'
                            : statusFilter === 'completed'
                              ? 'No completed purchase orders'
                              : 'No purchase orders'}
                    </Text>
                </Flex>
            ) : (
                <>
                    <PurchaseOrdersTable
                        requests={requests}
                        focusedPurchaseOrderId={purchaseOrderId}
                        canOpenOrder={canOpenOrder}
                        dirtyByOrder={dirtyByOrder}
                        daysByOrder={daysByOrder}
                        selectedByOrder={selectedByOrder}
                        selectedTotal={selectedTotal}
                        onSetPartAction={setPartAction}
                        onSetDays={setDays}
                        onTogglePartSelected={togglePartSelected}
                        onToggleSelectAllPending={toggleSelectAllPending}
                        onClearSelection={clearSelection}
                        onBulkAction={applyBulkAction}
                    />
                    <Box flexShrink={0} borderTop="1px solid" borderColor="border.subtle">
                        <Pagination
                            currentPage={pageIndex + 1}
                            totalPages={totalPages}
                            hasNextPage={hasNextPage}
                            onPageChange={handlePageChange}
                            isLoading={feed.isFetching}
                        />
                    </Box>
                </>
            )}
        </Flex>
    );
}
